import axios from "axios";
import env from "../variables/config";

axios.defaults.baseURL = env.apiUrl;

const config = () => ({
  headers: { Authorization: "Bearer " + localStorage.getItem("token") },
});

// ─── Types ─────────────────────────────────────────────────────────────────────

export type ZoneTargetModel = "Event" | "Person";

export type ZoneRunStatus =
  | "pending"
  | "running"
  | "completed"
  | "stopped"
  | "budget_reached"
  | "expired"
  | "error";

export interface ZoneServiceCatalogItem {
  key: string;
  label: string;
  targetModel: ZoneTargetModel;
  /** null when the external API is untracked and unpriced — the UI shows "—", not "0". */
  apiService: string | null;
  callsPerItem: number;
  nominalItemsPerHour: number;
  pacingNote: string;
  /** Registry keys switched off while this service is focused. */
  pausesOnFocus: string[];
}

export interface ZoneServicesCatalog {
  services: ZoneServiceCatalogItem[];
  maxRadiusKm: number;
  defaultTtlHours: number;
  maxTtlHours: number;
}

export interface ZoneEnrichmentBreakdown {
  type: string;
  total: number;
  byStatus: Record<string, number>;
}

export interface ZoneServiceSummary {
  key: string;
  label: string;
  targetModel: ZoneTargetModel;
  enabled: boolean;
  eligibleInZone: number;
  apiService: string | null;
  callsPerItem: number;
}

/** "full" re-derives the zone; "counts" re-counts a cached id set — the periodic refresh. */
export type ZoneReportMode = "full" | "counts";

export interface ZoneReport {
  center: [number, number];
  radiusKm: number;
  establishments: {
    total: number;
    geocoded: number;
    viaCentroid: number;
    truncated: boolean;
  };
  events: { total: number; enrichments: ZoneEnrichmentBreakdown[] };
  people: { total: number; enrichments: ZoneEnrichmentBreakdown[] };
  services: ZoneServiceSummary[];
  computedAt: string;
  computeMs: number;
  mode: ZoneReportMode;
  /** When the id set behind these counts was derived — a "counts" refresh reuses it. */
  setsResolvedAt: string;
}

export type ZoneLogLevel = "info" | "warn" | "error";

export interface ZoneLogEntry {
  _id: string;
  at: string;
  level: ZoneLogLevel;
  message: string;
  data?: Record<string, any>;
}

export interface StoredZoneSelection {
  center: [number, number];
  radiusKm: number;
  savedAt: string;
}

export interface ZoneEstimate {
  serviceKey: string;
  label: string;
  targetModel: ZoneTargetModel;
  totalInZone: number;
  selectedPct: number;
  selectedCount: number;
  callsPerItem: number;
  calls: number;
  apiService: string | null;
  costPerCall: number | null;
  estimatedUsd: number | null;
  itemsPerHour: number;
  rateSource: "observed" | "nominal";
  estimatedHours: number | null;
  pacingNote: string;
}

export interface ZonePausedService {
  key: string;
  previousEnabled: boolean;
}

export interface ZoneRun {
  _id: string;
  serviceKey: string;
  label: string;
  targetModel: ZoneTargetModel;
  center: [number, number];
  radiusKm: number;
  selectedPct: number;
  totalInZone: number;
  targetCount: number;
  cursor: number;
  progressPct: number;
  apiService: string | null;
  callsPerItem: number;
  estimatedUsd: number | null;
  spentUsd: number;
  budgetUsd: number | null;
  pausedServices: ZonePausedService[];
  /** Set when the run had to switch the focused service on; restored when the run ends. */
  enabledService: ZonePausedService | null;
  focusUntil: string;
  status: ZoneRunStatus;
  error: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdBy: string | null;
}

export interface ZonePrice {
  serviceName: string;
  costPerCall: number | null;
  isCustom: boolean;
  keyCount: number;
}

export interface ZoneSelection {
  center: [number, number];
  radiusKm: number;
}

// ─── Calls ─────────────────────────────────────────────────────────────────────

export const getZoneServices = async (): Promise<ZoneServicesCatalog> => {
  const { data } = await axios.get("/admin/zone/services", config());
  return data;
};

export const getZoneReport = async (
  selection: ZoneSelection,
  mode: ZoneReportMode = "full"
): Promise<ZoneReport> => {
  const { data } = await axios.post("/admin/zone/report", { ...selection, mode }, config());
  return data;
};

/** Only lines newer than `since`, so a poll stays small however long the run lasts. */
export const getZoneRunLogs = async (
  runId: string,
  since?: string
): Promise<ZoneLogEntry[]> => {
  const qs = since ? `?since=${encodeURIComponent(since)}` : "";
  const { data } = await axios.get(`/admin/zone/runs/${runId}/logs${qs}`, config());
  return data.entries;
};

export const getSavedZoneSelection = async (): Promise<StoredZoneSelection | null> => {
  const { data } = await axios.get("/admin/zone/selection", config());
  return data.selection;
};

export const saveZoneSelection = async (
  selection: ZoneSelection
): Promise<StoredZoneSelection> => {
  const { data } = await axios.put("/admin/zone/selection", selection, config());
  return data.selection;
};

export const estimateZoneRun = async (
  selection: ZoneSelection,
  serviceKey: string,
  options: { selectedPct?: number; limit?: number } = {}
): Promise<ZoneEstimate> => {
  const { data } = await axios.post(
    "/admin/zone/estimate",
    { ...selection, serviceKey, ...options },
    config()
  );
  return data;
};

export const getActiveZoneRun = async (): Promise<ZoneRun | null> => {
  const { data } = await axios.get("/admin/zone/runs/active", config());
  return data.run;
};

export const getZoneRuns = async (limit = 20): Promise<ZoneRun[]> => {
  const { data } = await axios.get(`/admin/zone/runs?limit=${limit}`, config());
  return data.runs;
};

export const startZoneRun = async (
  selection: ZoneSelection,
  serviceKey: string,
  options: { selectedPct?: number; limit?: number; budgetUsd?: number | null; ttlHours?: number } = {}
): Promise<ZoneRun> => {
  const { data } = await axios.post(
    "/admin/zone/runs",
    { ...selection, serviceKey, ...options },
    config()
  );
  return data.run;
};

export const stopZoneRun = async (id: string): Promise<ZoneRun> => {
  const { data } = await axios.post(`/admin/zone/runs/${id}/stop`, {}, config());
  return data.run;
};

export const getZonePrices = async (): Promise<ZonePrice[]> => {
  const { data } = await axios.get("/admin/zone/prices", config());
  return data.prices;
};

export const setZonePrice = async (
  serviceName: string,
  costPerCall: number | null
): Promise<ZonePrice[]> => {
  const { data } = await axios.patch(
    `/admin/zone/prices/${encodeURIComponent(serviceName)}`,
    { costPerCall },
    config()
  );
  return data.prices;
};
